import { useEffect, useState } from "react";
import axios from "axios";
import { Users } from "lucide-react";
import { useT } from "@/lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public subscriber-count social proof.
 *
 * UX strategy:
 *   - Below 25 subscribers: do NOT show the number (would hurt conversion).
 *     Show an aspirational, warm copy instead.
 *   - From 25 onwards: show "Únete a 25+ personas que ya la reciben…" using
 *     the *rounded* number so the count only ticks up every 5 signups (less
 *     anxious, more believable, no "10 → 11" awkwardness).
 *
 * Variant:
 *   - "hero"  → big number for the top of /newsletter (default)
 *   - "inline" → smaller line for use inside articles or forms
 */
const SubscriberCount = ({ variant = "hero" }) => {
  const t = useT();
  const [rounded, setRounded] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/newsletter/public-count`)
      .then((r) => { if (!cancelled) setRounded(r.data.rounded ?? 0); })
      .catch(() => { /* silent — block hides itself if anything fails */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  const showCount = rounded != null && rounded >= 25;

  if (variant === "inline") {
    return (
      <p
        className="text-[13px] text-charcoal/65 inline-flex items-center gap-2"
        data-testid="subscriber-count-inline"
      >
        <Users size={13} className="text-terracotta" />
        {showCount
          ? t("subscriberCount.inlineWith").replace("{n}", `${rounded}+`)
          : t("subscriberCount.inlineEmpty")}
      </p>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-4 bg-cream/60 border border-sand/70 rounded-full pl-5 pr-7 py-3"
      data-testid="subscriber-count-hero"
    >
      <span className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta shrink-0">
        <Users size={17} />
      </span>
      <div className="text-left">
        {showCount ? (
          <>
            <p className="font-serif text-2xl md:text-[28px] text-charcoal leading-none">
              <span className="text-terracotta">{rounded}+</span> {t("subscriberCount.peopleLabel")}
            </p>
            <p className="text-[12.5px] text-charcoal/60 mt-1">{t("subscriberCount.heroSub")}</p>
          </>
        ) : (
          <>
            <p className="font-serif text-xl text-charcoal leading-tight">
              {t("subscriberCount.heroEmptyTitle")}
            </p>
            <p className="text-[12.5px] text-charcoal/60 mt-1">{t("subscriberCount.heroEmptySub")}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriberCount;
